import { ArrowBackIcon, ArrowForwardIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Skeleton,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { AssessmentTemplateDto, bffApi } from "../../../../autogen/bff-api";
import { OrganizationAssessmentForm } from "./OrganizationAssessmentForm";

interface OrganizationAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: string;
  organizationEntryId?: string;
  organizationName: string;
  onAssessmentComplete: () => void;
  selectedTemplateId?: string | null;
}

export const OrganizationAssessmentModal = ({
  isOpen,
  onClose,
  organizationId,
  organizationEntryId,
  organizationName,
  onAssessmentComplete,
  selectedTemplateId: initialTemplateId,
}: OrganizationAssessmentModalProps) => {
  // If a template is pre-selected, skip the template selection step
  const [showTemplateSelection, setShowTemplateSelection] = useState(initialTemplateId ? false : true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId || "");
  const [selectedTemplate, setSelectedTemplate] = useState<AssessmentTemplateDto | null>(null);

  const { data: templateData, isLoading: isLoadingTemplates } = bffApi.useListAssessmentTemplatesQuery(
    isOpen ? {} : skipToken
  );

  const latestTemplates = useMemo(() => {
    if (!templateData?.templates?.length) return [];
    const templatesByFamily: Record<string, AssessmentTemplateDto[]> = {};
    templateData.templates.forEach((template) => {
      const familyId = template.templateId;
      if (familyId && !templatesByFamily[familyId]) {
        templatesByFamily[familyId] = [];
      }
      if (familyId) {
        templatesByFamily[familyId].push(template);
      } else {
        if (!templatesByFamily[template.id]) {
          templatesByFamily[template.id] = [];
        }
        templatesByFamily[template.id].push(template);
      }
    });

    return Object.values(templatesByFamily).map((familyTemplates) => {
      return familyTemplates.sort((a, b) => {
        const versionA = a.templateVersion || 0;
        const versionB = b.templateVersion || 0;
        return versionB - versionA;
      })[0];
    });
  }, [templateData]);

  const handleAssessmentComplete = () => {
    onAssessmentComplete();
    resetState();
    onClose();
  };

  const handleTemplateSelectionSubmit = () => {
    if (selectedTemplate) {
      setShowTemplateSelection(false);
    }
  };

  const resetState = () => {
    setShowTemplateSelection(true);
    setSelectedTemplateId("");
    setSelectedTemplate(null);
  };

  // Reset state when modal is closed
  const handleClose = () => {
    resetState();
    onClose();
  };

  // When modal opens/closes or initialTemplateId changes
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setShowTemplateSelection(initialTemplateId ? false : true);
      setSelectedTemplateId(initialTemplateId || "");
      setSelectedTemplate(null);
    } else if (initialTemplateId && latestTemplates.length > 0) {
      // When opening with a pre-selected template
      setSelectedTemplateId(initialTemplateId);
      setShowTemplateSelection(false);

      // Find the latest template with the matching templateId from the already sorted latestTemplates
      const latestTemplate = latestTemplates.find((t) => t.templateId === initialTemplateId);
      if (latestTemplate) {
        setSelectedTemplate(latestTemplate);
      }
    }
  }, [isOpen, initialTemplateId, latestTemplates]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader borderBottomWidth="1px" borderColor="smBorder" pb={4} mb={2}>
          {t("Organization Assessment")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {showTemplateSelection ? (
            <Box py={4}>
              <Text fontSize="lg" fontWeight="medium" mb={4}>
                {t("Select a Template")}
              </Text>

              {isLoadingTemplates && (
                <Stack spacing={3}>
                  {[1, 2, 3].map((i) => (
                    <Card key={i} variant="outline" borderColor="smBorder">
                      <CardBody>
                        <Flex>
                          <Skeleton height="20px" width="20px" mr={3} mt={1} />
                          <Box width="full">
                            <Skeleton height="24px" width="70%" mb={2} />
                            <Skeleton height="16px" width="90%" mb={2} />
                            <Skeleton height="16px" width="40%" mb={1} />
                            <Stack spacing={1} mt={2}>
                              <Skeleton height="16px" width="60%" />
                              <Skeleton height="16px" width="50%" />
                              <Skeleton height="16px" width="55%" />
                            </Stack>
                          </Box>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </Stack>
              )}

              {!isLoadingTemplates && templateData?.templates?.length ? (
                <Box>
                  <RadioGroup
                    value={selectedTemplateId}
                    onChange={(value) => {
                      setSelectedTemplateId(value);
                      const template = latestTemplates.find((t) => t.id === value);
                      if (template) {
                        setSelectedTemplate(template);
                      }
                    }}
                  >
                    <Stack spacing={3}>
                      {latestTemplates.map((template) => (
                        <Card
                          key={template.id}
                          variant="outline"
                          cursor="pointer"
                          onClick={() => {
                            setSelectedTemplateId(template.id);
                            setSelectedTemplate(template);
                          }}
                          borderColor="smBorder"
                        >
                          <CardBody>
                            <Flex mb={2}>
                              <Radio
                                value={template.id}
                                mr={3}
                                isChecked={selectedTemplateId === template.id}
                                alignSelf="flex-start"
                                mt={1}
                              />
                              <Box>
                                <Text
                                  fontWeight={selectedTemplateId === template.id ? "bold" : "semibold"}
                                  fontSize="md"
                                >
                                  {template.name}
                                </Text>
                                {template.description && (
                                  <Text fontSize="sm" color="gray.500" mb={2}>
                                    {template.description}
                                  </Text>
                                )}
                                {template.dimensions && template.dimensions.length > 0 && (
                                  <Box mt={2}>
                                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                                      {t("Dimensions")}:
                                    </Text>
                                    <Stack spacing={1}>
                                      {template.dimensions.map((dim) => (
                                        <Text key={dim.id} fontSize="sm" color="smMuted">
                                          • {dim.name || dim.id}
                                        </Text>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </Box>
                            </Flex>
                          </CardBody>
                        </Card>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Box>
              ) : null}

              {!isLoadingTemplates && (!templateData?.templates || templateData.templates.length === 0) && (
                <Box>
                  <Text align="center" fontStyle="italic" color="smMuted">
                    {t("No assessment templates available")}. {t("Please create templates first")}.
                  </Text>
                </Box>
              )}
            </Box>
          ) : selectedTemplate ? (
            <OrganizationAssessmentForm
              organizationId={organizationId}
              organizationEntryId={organizationEntryId}
              organizationName={organizationName}
              template={selectedTemplate}
              onAssessmentComplete={handleAssessmentComplete}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Spinner size="xl" />
            </Box>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="smBorder" pt={4} mt={2}>
          {showTemplateSelection ? (
            <>
              <Button variant="ghost" onClick={handleClose} mr="auto" leftIcon={<CloseIcon fontSize="xs" />}>
                {t("Cancel")}
              </Button>
              <Button
                colorScheme="teal"
                onClick={handleTemplateSelectionSubmit}
                isDisabled={!selectedTemplate}
                size="md"
                rightIcon={<ArrowForwardIcon />}
              >
                {t("Next")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setShowTemplateSelection(true)}
                mr="auto"
                leftIcon={<ArrowBackIcon />}
              >
                {t("Back to Templates")}
              </Button>
              <Button variant="ghost" onClick={handleClose} mr={3} leftIcon={<CloseIcon fontSize="xs" />}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="teal" form="assessment-form" type="submit" size="md" rightIcon={<CheckIcon />}>
                {t("Save")}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
